import React, {useEffect, useState} from 'react';

import styles from './pref_app_version_files.module.css';
import Time from '../../services/time.service';
import IAppVersionFile from '../../models/app_version_file.model';
import Dropzone from 'react-dropzone'
import { observer } from 'mobx-react';
import Popup, { PopupInfo } from '../common/popup.component';
import trash from '../../img/preferences/trash.svg';
import appStore from '../../stores/app.store';
import { Platform } from '../../models/platform.model';

const PrefAppVersionFiles = observer(() => {
  const [file, setFile] = useState<File>();
  const [appVersion, setAppVersion] = useState("");
  const [build, setBuild] = useState("");
  const [sourceMapBaseUrl, setSourceMapBaseUrl] = useState(appStore.app?.sourceMapBaseUrl ?? '');
  const [baseUrlChanged, setBaseUrlChanged] = useState(false);

  useEffect(() => {
    appStore.getAppVersionFiles();
  }, []); // Empty dependency array - only fetch once on mount

  useEffect(() => {
    setSourceMapBaseUrl(appStore.app?.sourceMapBaseUrl ?? '');
    setBaseUrlChanged(false);
  }, [appStore.app?.sourceMapBaseUrl]);

  const handleSubmission = async (event?: React.FormEvent<HTMLButtonElement>) => {
    if (event) event.preventDefault();
    const platform = appStore.app?.platform;
    const isDsymPlatform = platform === Platform.IOS || platform === Platform.TVOS;
    const fileType = isDsymPlatform ? 'dsym' : 'sourcemap';

    // For source maps, use default version/build if not provided
    const finalVersion = (!isDsymPlatform && !appVersion) ? 'latest' : appVersion;
    const finalBuild = (!isDsymPlatform && !build) ? '1' : build;

    await appStore.uploadSymbolFile(file!, finalVersion, finalBuild, fileType);
    setFile(undefined);
    setAppVersion("");
    setBuild("");
  }
  
  const onTrash = (appVersionFile: IAppVersionFile) => {
    const displayName = appVersionFile.versionInfo
      ? `${appVersionFile.versionInfo.version} (${appVersionFile.versionInfo.build})`
      : appVersionFile.name;

    const info: PopupInfo = {
      title: 'Delete',
      msg: `Are you sure that you want to delete the file ${displayName}?`,
      ok: 'Delete',
      okCallback: () => appStore.deleteAppVersionFile(appVersionFile._id),
      cancel: 'Cancel'
    };
    Popup.create(info);
  }  

  const handleChange = (event: React.FormEvent<HTMLInputElement>) => {
    const {name, value} = event.currentTarget;
    switch (name) {
      case 'build':
        setBuild(value);
        break;
      case 'appVersion':
        setAppVersion(value);
        break;
      default:
    }
  }

  const onDrop = (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    setFile(file);
  }

  const handleBaseUrlChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSourceMapBaseUrl(value);
    setBaseUrlChanged(value !== (appStore.app?.sourceMapBaseUrl ?? ''));
  };

  const handleBaseUrlSubmit = () => {
    if (sourceMapBaseUrl && !sourceMapBaseUrl.startsWith('https://')) {
      Popup.create({
        title: 'Invalid URL',
        msg: 'The base URL must use HTTPS.',
        ok: 'OK',
        okCallback: () => {},
      });
      return;
    }

    const info: PopupInfo = {
      title: 'Save Auto-Fetch URL',
      msg: sourceMapBaseUrl
        ? `Shipbook will automatically fetch source maps from: ${sourceMapBaseUrl}`
        : 'Remove the auto-fetch source map URL?',
      ok: 'Save',
      okCallback: async () => {
        const app = { ...appStore.app!, sourceMapBaseUrl: sourceMapBaseUrl || undefined };
        await appStore.updateApp(app);
        setBaseUrlChanged(false);
      },
      cancel: 'Cancel',
    };
    Popup.create(info);
  };

  const handleBaseUrlClear = () => {
    setSourceMapBaseUrl('');
    setBaseUrlChanged(true);
  };

  const {appVersionFiles} = appStore;
  const platform = appStore.app?.platform;

  // Determine if this is an iOS/tvOS app (dSYM) or JS app (Source Maps)
  const isDsymPlatform = platform === Platform.IOS || platform === Platform.TVOS;
  const isSourceMapPlatform = platform === Platform.REACT_NATIVE || platform === Platform.BROWSER;

  // For dSYM: require version and build
  // For source maps: only need the file
  const disabled = isDsymPlatform
    ? !(file && appVersion.length > 0 && build.length > 0)
    : !file;

  // Filter files by type based on platform
  const dsymFiles = appVersionFiles.filter(f => f.fileType !== 'sourcemap');
  const sourcemapFiles = appVersionFiles.filter(f => f.fileType === 'sourcemap');

  return (
    <div className={styles.component}>
      {/* dSYM Section - iOS/tvOS only */}
      {isDsymPlatform && (
        <>
          <div className={styles.subTitle}>dSYM Files</div>
          <div className={styles.bodyHeader}>
            Upload dSYM files for iOS/macOS apps to symbolicate crash reports.
            <a href="https://docs.shipbook.io/ios-log-integration#upload-the-dsym-file"> Learn more</a>
          </div>

          {dsymFiles.map((appVersionFile) =>
            <div key={appVersionFile._id} className={styles.dsym}>
              <div> {new Time(appVersionFile.createdAt).getShortDate()} </div>
              <div> {appVersionFile.versionInfo?.version} ({appVersionFile.versionInfo?.build})</div>
              <div> {appVersionFile.name}</div>
              <img alt="trash" className={styles.trash} src={trash} onClick={() => onTrash(appVersionFile)} />
            </div>
          )}

          <div className={styles.breakLine} />
          <div className={styles.addFile}>
            <Dropzone onDrop={onDrop} >
              {({getRootProps, getInputProps}) => (
                <div {...getRootProps({className: styles.dragDrop})}>
                  <input {...getInputProps()} />
                  {file ? <div>{file.name}</div> : <div>Drag &amp; Drop or <span>Upload dSYM</span></div> }
                </div>
              )}
            </Dropzone>
            <input className={styles.input} type="text" name="appVersion" value={appVersion} onChange={handleChange} placeholder="App version" />
            <input className={styles.input} type="text" name="build" value={build} onChange={handleChange} placeholder="build" />
            <div className={styles.space}/>
            <button onClick={handleSubmission} disabled={disabled} >Add dSYM</button>
          </div>
        </>
      )}

      {/* Source Maps Section - React Native/Browser only */}
      {isSourceMapPlatform && (
        <>
          <div className={styles.subTitle}>Source Maps</div>
          <div className={styles.bodyHeader}>
            Upload source map files (.js.map) for each JavaScript bundle to resolve minified stack traces.
            <a href="https://docs.shipbook.io/react-native-log-integration#uploading-source-maps"> Learn more</a>
          </div>

          {sourcemapFiles.map((appVersionFile) =>
            <div key={appVersionFile._id} className={styles.dsym}>
              <div> {new Time(appVersionFile.createdAt).getShortDate()} </div>
              <div> {appVersionFile.name}</div>
              <img alt="trash" className={styles.trash} src={trash} onClick={() => onTrash(appVersionFile)} />
            </div>
          )}

          <div className={styles.breakLine} />

          <div className={styles.subTitle}>Auto-Fetch Source Maps</div>
          <div className={styles.bodyHeader}>
            Configure a base URL where your source maps are hosted. When new bundle files
            are detected, Shipbook will automatically fetch the corresponding <code>.map</code> file.
          </div>
          <div className={styles.baseUrlRow}>
            <input
              className={styles.baseUrlInput}
              type="url"
              value={sourceMapBaseUrl}
              onChange={handleBaseUrlChange}
              placeholder="https://cdn.example.com/assets"
            />
            <button onClick={handleBaseUrlSubmit} disabled={!baseUrlChanged}>Save</button>
            {sourceMapBaseUrl && (
              <button onClick={handleBaseUrlClear}>Clear</button>
            )}
          </div>

          <div className={styles.breakLine} />

          <div className={styles.subTitle}>Manual Upload</div>
          <div className={styles.addFile}>
            <Dropzone onDrop={onDrop} >
              {({getRootProps, getInputProps}) => (
                <div {...getRootProps({className: styles.dragDrop})}>
                  <input {...getInputProps()} />
                  {file ? <div>{file.name}</div> : <div>Drag &amp; Drop or <span>Upload Source Map</span> (.js.map)</div> }
                </div>
              )}
            </Dropzone>
            <div className={styles.space}/>
            <button onClick={handleSubmission} disabled={disabled} >Add Source Map</button>
          </div>
        </>
      )}
    </div>
  )
});


export default PrefAppVersionFiles;